import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';
import { getPaperTexture } from '../paper-texture.js';

// Shared-rig contract (see the framing addendum): cloth on a loom, lit by
// nothing.
const RIG = 'forbidden';
export { RIG };

// Orchestrator registry entry (see visualizer-registry.js).
export const PROFILE = {
  id: 'loom',
  family: 'paper',
  energyLevel: 0.30,
  bpmMin: 50,
  bpmMax: 165,
  movement: 0.25,
  danceability: 0.30,
  tonality: 0.80,
  prefers: { dynamics: [0.30, 1.0], percussiveness: [0.0, 0.70] },
  enabled: true
};

// Loom: a warp held vertically, a weft built up row by row as the track
// plays.
//
// This is the ONE visualizer in the library where the image is CUMULATIVE.
// Everything else here renders an instantaneous state - what the music is
// doing right now - and forgets it. Here nothing is ever erased, nothing
// blinks, nothing loops: at the end of a track the whole cloth is on screen
// and you are looking at the track itself, woven. A verse and a chorus are
// different bands of colour in the same piece of fabric.
//
// Fourth of the paper family after sumi.js, halftone.js and manuscript.js,
// and it follows their rules exactly: matte flats on a light ground, NORMAL
// blending, nothing anywhere near the bloom pass's threshold.
//
// THE CROSSING IS THE WHOLE THING. A weft row that merely sits on top of the
// warp is a set of stripes, not cloth. So the weft is genuinely ABSENT where
// it passes under a warp thread - a discard, not an alpha fade - and the warp
// (drawn underneath, opaque) shows through the hole. Which of the two is in
// front at a given intersection is the weave, and the weave is what makes a
// twill look like denim and a plain weave look like canvas.

// ---------------------------------------------------------------------------
// Quality tiers. Warp counts here are the CEILING for that tier;
// character density interpolates between WARP_MIN and it (see _resolveWarpCount).
const TIER = {
  0: { maxWarp: 48, rows: 70, thicknessNoise: false, selvedge: true },
  1: { maxWarp: 64, rows: 90, thicknessNoise: true, selvedge: true },
  2: { maxWarp: 80, rows: 100, thicknessNoise: true, selvedge: true }
};
const DEFAULT_TIER = 1;
const WARP_MIN = 48;

// ---------------------------------------------------------------------------
// The warp. Fixed for the whole track: it is the support, not the subject.
const WARP_WIDTH_PX_MIN = 4;
const WARP_WIDTH_PX_MAX = 7;
// The weft is cut over a band this wide (half-width, in pixels) wherever the
// weave says it passes UNDER. Deliberately the half-width of the NARROWEST
// possible warp thread rather than of the actual one: the hole is then always
// covered by the thread that is supposed to be filling it, whatever width that
// particular thread drew at, so no sliver of paper can ever appear between the
// two. The weft reads as tucking under the warp's own edges, which is what it
// physically does.
const WEFT_CUT_HALF_PX = WARP_WIDTH_PX_MIN * 0.5;

// ---------------------------------------------------------------------------
// The weft. Thickness is a fraction of the row pitch, so a coarse cloth (few
// rows) gets proportionally fatter thread and the weave stays legible at every
// tier.
const THREAD_PITCH_FRAC_MIN = 0.50; // bands.low -> 0: a fine, airy row
const THREAD_PITCH_FRAC_MAX = 0.92; // bands.low -> 1: a heavy row, nearly closed
// "Un tissu a main n'a pas de rangees parfaitement droites."
const TENSION_JITTER_PX = 1.5;
const THICKNESS_VARIATION = 0.12;   // +-12% along a single row, 1D noise
const SELVEDGE_THICKEN = 0.55;      // the border is denser than the field

// ---------------------------------------------------------------------------
// Weave. Decided once per track and never changed - real cloth does not change
// armure halfway up.
const WEAVE_PLAIN = 0;  // toile:  1 over, 1 under
const WEAVE_TWILL = 1;  // serge:  2 over, 1 under, shifted one warp per row
const WEAVE_SATIN = 2;  // satin:  4 over, 1 under, irregular shift
const SYNCOPATION_TWILL_ABOVE = 0.33;
const SYNCOPATION_SATIN_ABOVE = 0.66;

// ---------------------------------------------------------------------------
// Colour. The palette is textile, not stage lighting. artwork-palette.js hands
// over five vividColors - the cover's own five most contrasted hues, each with
// its saturation boosted and its lightness clamped into [0.38, 0.62] (see
// boostColor there) - and here they become five cones of dyed yarn on a shelf,
// pulled down into a matte range first. All five are used: see _buildShelf.
const WEFT_L_MIN = 0.26;
const WEFT_L_MAX = 0.48;
const WEFT_SAT_MAX = 0.85;
const WEFT_SAT_SCALE_MIN = 0.35; // energy -> 0: an undyed, greyed row
const WEFT_SAT_SCALE_MAX = 1.05; // energy -> 1: the dye at full strength
// The palette's own lightness spread is stretched across the matte band above,
// so the cover's tonal ORDER survives into the cloth - its darkest colour
// really is the darkest yarn. Stretched PROPORTIONALLY though, against a floor
// on the spread: five colours that happen to sit within 0.02 of each other in
// lightness must not come out looking like a deliberate light-to-dark range.
const PALETTE_L_SPAN_FLOOR = 0.12;
// How hard the loudest band pulls the row's colour toward its own end of the
// shelf. Raising each band's weight to this power before taking the centroid
// is what makes a genuinely low-dominant instant land ON the warmest yarn
// rather than three-fifths of the way toward it - without it the weighted
// average sits near the middle of the shelf almost always, and the two
// colours at the ends of the cover's own range would never be woven at all.
const BAND_DOMINANCE = 2.2;

// Undyed / vegetable-dye fallback, used when the cover gave no palette at all.
// Warm to cool, in the order a loom would have them on the shelf: madder,
// ochre, olive, indigo, slate.
const FALLBACK_PALETTE = [0xa33f2b, 0xc98a2e, 0x7d7a3a, 0x2f4b7c, 0x5d6b78];

// ---------------------------------------------------------------------------
// Paper and light level.
//
// LUM_CAP_LINEAR is the hard ceiling every colour in this file is built under.
// The number is LINEAR scene light, while the brief's 0.74 is what a
// screenshot measures - and the two are a long way apart, because the frame
// goes through ACES tone mapping at exposure 1.1 and then an sRGB encode
// before anything is measured (scene-manager.js: toneMapping + OutputPass).
// Pushing values through that chain analytically: 0.394 linear arrives at
// exactly 0.740, and 0.37 arrives at 0.726. 0.37 is what sumi.js already
// settled on from real captures (it measured 0.727 for this value), so the
// two agree and this file adopts the same number rather than a second,
// independently-tuned one.
const LUM_CAP_LINEAR = 0.37;

// The paper is REBUILT rather than clipped. Clamping the texture with a min()
// (what manuscript.js does) flattens it: the sheet's linear values run 0.53 to
// 0.99 with a mean of 0.819, so any ceiling under 0.53 turns the whole thing
// into one flat value and the grain disappears - and the brief asks
// specifically for the grain to stay perceptible. So the texture's LUMINANCE
// is re-levelled around its own measured mean and re-tinted: the grain keeps
// its shape, the level is set by us, and the peak is known in advance
// (0.30 + (0.991 - 0.819) * 0.50 = 0.386 linear, 0.735 displayed) instead of
// being whatever the texture happens to contain.
const PAPER_MEAN_LINEAR = 0.819; // measured on public/textures/papier.*.webp
const PAPER_LEVEL_LINEAR = 0.30; // ~0.671 displayed - the light warm ground
const PAPER_GRAIN_GAIN = 0.50;
const PAPER_TINT_HEX = 0xf2ede2;

// "Une teinte neutre chaude, tres proche du papier mais un peu plus sombre."
const WARP_TINT_HEX = 0xe6d9c2;
const WARP_LEVEL_LINEAR = PAPER_LEVEL_LINEAR * 0.72; // ~0.573 displayed

// ---------------------------------------------------------------------------
// Timing.
const ROLL_OUT_S = 2.0;      // the finished cloth winding off the bottom
const ROLL_OUT_NDC = 2.15;   // far enough that the top row clears the frame
const RADIO_ROW_S = 2.5;     // no duration to divide up - one row every 2.5s
const FASTWEAVE_S = 20;      // ?fastweave=1
const SCROLL_TAU_S = 0.55;   // how fast uClothOffset chases its target
// The analysis a track's armure is chosen from (character.syncopation) does
// not exist on the frame the track starts - it resolves a few seconds in, once
// radiopi answers. Dressing the loom at frame 1 therefore picks the FALLBACK
// weave essentially every time, which quietly throws away the one piece of
// reactivity that is meant to be structural here.
//
// So a loom dressed without character data gets exactly one re-dress, and only
// while there is almost nothing on it: the few picks already made are taken
// off and the piece starts again properly sleyed. Past this many rows the
// armure it has is the armure it keeps - cloth on a loom is not re-dressed,
// and an armure that changed halfway up is the one thing this visualizer most
// obviously must never do.
const DRESS_GRACE_ROWS = 4;

// A seek can move progress by many rows at once. Rows are still woven one by
// one (each is a real moment with its own colour), just capped per frame so a
// scrub never turns into one enormous burst of buffer uploads.
const MAX_ROWS_PER_FRAME = 8;

// "Reduis l'opacite de la trame de 45% dans la zone" - the paper family's
// inversion: attenuating here means letting more of the light ground through,
// so the cover sits on a paler patch of cloth, like a label sewn on.
const SAFE_ZONE_WEFT_OPACITY = 0.55;

function clamp(x, lo, hi) {
  return Math.max(lo, Math.min(hi, x));
}

function clamp01(x) {
  return Math.max(0, Math.min(1, x));
}

function lerp(a, b, t) {
  return a + (b - a) * t;
}

function urlParam(name) {
  if (typeof location === 'undefined') return null;
  return new URLSearchParams(location.search).get(name);
}

// --- colour helpers ---------------------------------------------------------
// Done here on plain numbers rather than through THREE.Color deliberately:
// THREE.ColorManagement's working space is linear and its HSL accessors are
// relative to it, so "lightness" through that path is not the sRGB lightness
// artwork-palette.js's own vivid colours were built with. These are the same
// conversions that file uses, run in the same direction.
function hexToRgb(hex) {
  return [((hex >> 16) & 255) / 255, ((hex >> 8) & 255) / 255, (hex & 255) / 255];
}

function srgbToLinear(c) {
  return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
}

function rgbToHsl(r, g, b) {
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  if (max === min) return [0, 0, l];
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h;
  if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
  else if (max === g) h = ((b - r) / d + 2) / 6;
  else h = ((r - g) / d + 4) / 6;
  return [h, s, l];
}

function hue2rgb(p, q, t) {
  if (t < 0) t += 1;
  if (t > 1) t -= 1;
  if (t < 1 / 6) return p + (q - p) * 6 * t;
  if (t < 1 / 2) return q;
  if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
  return p;
}

function hslToRgb(h, s, l) {
  if (s === 0) return [l, l, l];
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  return [hue2rgb(p, q, h + 1 / 3), hue2rgb(p, q, h), hue2rgb(p, q, h - 1 / 3)];
}

// Same coefficients sumi.js caps against, applied to the same linear values,
// so "under the ceiling" means the identical thing across the paper family.
function linearLuminance(r, g, b) {
  return 0.299 * r + 0.587 * g + 0.114 * b;
}

// A hue-preserving cap: the whole colour is scaled, never clipped per channel,
// so a saturated dye stays that dye instead of drifting toward whichever
// channel survived the clip.
function capLuminance(out) {
  const lum = linearLuminance(out[0], out[1], out[2]);
  if (lum > LUM_CAP_LINEAR) {
    const k = LUM_CAP_LINEAR / lum;
    out[0] *= k; out[1] *= k; out[2] *= k;
  }
  return out;
}

// Normalized to luminance 1 so multiplying by a LEVEL gives that exact
// luminance - the ceiling then holds by construction rather than by measurement.
function tintOf(hex) {
  const c = hexToRgb(hex).map(srgbToLinear);
  const lum = linearLuminance(c[0], c[1], c[2]);
  return lum > 0 ? [c[0] / lum, c[1] / lum, c[2] / lum] : [1, 1, 1];
}

// djb2, the same one sonos-visual-profile.js hashes track identity with -
// used here only to give a track with no analysis a STABLE weave and warp
// count rather than a random one that would change on every reload.
function hashSeed(str) {
  let h = 5381;
  for (let i = 0; i < String(str).length; i++) h = ((h << 5) + h + String(str).charCodeAt(i)) | 0;
  return (h >>> 0) / 4294967296;
}

// --- shaders ----------------------------------------------------------------

// Every mesh here is positioned directly in clip space and never looks at the
// camera, the same choice manuscript.js made and for the same reason: this is
// a flat object pinned to the frame, and scene-manager's shared Ken Burns
// drift sliding a loom around would read as the loom being loose.
const paperVertexShader = `
  precision highp float;
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = vec4(position.xy * 2.0, 0.0, 1.0);
  }
`;

const paperFragmentShader = `
  precision mediump float;

  uniform sampler2D uPaper;
  uniform float uHasPaper;
  uniform vec3 uPaperTint;
  uniform float uPaperLevel;
  uniform float uPaperGrain;
  uniform float uPaperMean;

  varying vec2 vUv;

  void main() {
    // Only the sheet's LUMINANCE is kept; its own faint colour cast is
    // replaced by uPaperTint. The texture is near-neutral to begin with
    // (measured mean 233.5/233.8/233.4), so nothing real is lost, and it
    // makes the output level exactly predictable.
    float g = uHasPaper > 0.5
      ? dot(texture2D(uPaper, vUv).rgb, vec3(0.299, 0.587, 0.114))
      : uPaperMean;
    float level = uPaperLevel + (g - uPaperMean) * uPaperGrain;
    gl_FragColor = vec4(uPaperTint * max(level, 0.0), 1.0);
  }
`;

const warpVertexShader = `
  precision highp float;

  attribute vec3 iWarp; // x centre in 0..1 across the frame, y half width px, z shade

  uniform float uPixelWidth;

  varying float vAcross;
  varying float vShade;

  void main() {
    vAcross = position.x * 2.0;
    vShade = iWarp.z;
    float halfU = iWarp.y / max(uPixelWidth, 1.0);
    float u = iWarp.x + vAcross * halfU;
    // 2.2 rather than 2.0: the warp runs off the top and the bottom of the
    // frame, because a warp is longer than the piece being woven on it.
    gl_Position = vec4(u * 2.0 - 1.0, position.y * 2.2, 0.0, 1.0);
  }
`;

const warpFragmentShader = `
  precision mediump float;

  uniform vec3 uWarpColor;

  varying float vAcross;
  varying float vShade;

  void main() {
    float edge = 1.0 - smoothstep(0.68, 1.0, abs(vAcross));
    if (edge <= 0.004) discard;
    gl_FragColor = vec4(uWarpColor * (0.93 + 0.14 * vShade), edge);
  }
`;

// Per-instance: one finished (or in-progress) weft row.
//   iRowData.x  absolute row index - drives BOTH the row's height in the cloth
//               and its place in the weave
//   iRowData.y  tension jitter, in clip-space y
//   iRowData.z  half thickness, in clip-space y
//   iRowData.w  seed for this row's own thickness noise
//   iColor      the row's colour, already linear and already under the ceiling
//
// The row index is unbounded on a radio stream (rows keep coming for as long
// as the station plays), so all of the position arithmetic is highp and the
// fragment shader is handed a pre-reduced 0..29 phase instead - 30 being
// lcm(2,3,5), the period every weave here repeats on. That keeps the default
// mediump in the fragment stage honest even after several thousand rows.
const weftVertexShader = `
  precision highp float;

  attribute vec4 iRowData;
  attribute vec3 iColor;

  uniform float uRowPitch;
  uniform float uHalfSlot;
  uniform float uClothOffset;
  uniform float uCurrentRow;

  varying highp vec2 vRowUv; // x across the frame 0..1, y offset from the row centre
  varying highp vec2 vNdc;
  varying vec3 vColor;
  varying float vHalf;
  varying float vSeed;
  varying float vPhase;
  varying float vDir;
  varying float vCurrent;
  // uRowPitch itself cannot be read by the fragment stage: a uniform of a
  // given name must carry the SAME precision in both stages, and this one
  // genuinely needs highp here (a row index times a pitch, and on a radio
  // stream the row index runs into the thousands). The fragment only wants
  // it to normalize the selvedge turn, so it is handed the value across
  // instead - mediump on both sides is ample for a number around 0.02.
  varying mediump float vPitch;

  void main() {
    float row = iRowData.x;
    float u = position.x + 0.5;
    float dy = position.y * 2.0 * uHalfSlot;
    float yc = -1.0 + (row + 0.5) * uRowPitch + iRowData.y + uClothOffset;
    float x = u * 2.0 - 1.0;
    float y = yc + dy;

    vRowUv = vec2(u, dy);
    vNdc = vec2(x, y);
    vColor = iColor;
    vHalf = iRowData.z;
    vSeed = iRowData.w;
    vPhase = mod(row, 30.0);
    // A real loom alternates: left to right, then right to left.
    vDir = mod(row, 2.0) < 0.5 ? 1.0 : -1.0;
    vCurrent = abs(row - uCurrentRow) < 0.5 ? 1.0 : 0.0;
    vPitch = uRowPitch;

    gl_Position = vec4(x, y, 0.0, 1.0);
  }
`;

const weftFragmentShader = `
  precision mediump float;

  // The default here is mediump, per the performance contract. The two
  // varyings carrying POSITION are the exception and they have to be: the
  // weft-cut band is about a thousandth of the frame wide, and comparing
  // values of that size against coordinates near 1.0 is exactly what a real
  // 16-bit fragment stage cannot do. Guarded rather than assumed - three
  // prefixes this shader with whatever precision the device actually has.
  #ifdef GL_FRAGMENT_PRECISION_HIGH
    #define POSP highp
  #else
    #define POSP mediump
  #endif

  uniform float uWarpCount;
  uniform float uWeftCutHalfU;
  uniform float uCurrentFrac;
  uniform float uPixelWidth;
  uniform float uPixelHeight;
  uniform float uThicknessNoise;
  uniform float uSelvedge;
  uniform float uWeave;

  uniform float uAspect;
  uniform vec2 uSafeCenter;
  uniform vec2 uSafeHalfSize;
  uniform float uSafeFeather;

  varying POSP vec2 vRowUv;
  varying POSP vec2 vNdc;
  varying vec3 vColor;
  varying float vHalf;
  varying float vSeed;
  varying float vPhase;
  varying float vDir;
  varying float vCurrent;
  varying mediump float vPitch;

  float hash11(float p) {
    p = fract(p * 0.1031);
    p *= p + 33.33;
    p *= p + p;
    return fract(p);
  }

  float noise11(float p) {
    float i = floor(p), f = fract(p);
    f = f * f * (3.0 - 2.0 * f);
    return mix(hash11(i), hash11(i + 1.0), f);
  }

  // 1 = this row passes OVER warp thread j, 0 = under it. A uniform branch,
  // not a compile-time define: the weave is fixed for a whole track, so this
  // is perfectly coherent across every fragment in the draw, and it costs a
  // per-track shader recompile to avoid nothing measurable.
  float weaveOver(float j, float r) {
    if (uWeave < 0.5) {
      // Toile. One over, one under, offset by a thread each row - the
      // checkerboard every plain-woven cloth is.
      return step(mod(j + r, 2.0), 0.5);
    }
    if (uWeave < 1.5) {
      // Serge. Two over, one under, and the -r is the entire point: the
      // float shifts one warp per row, which is what lines the floats up
      // into the diagonal wale you can see on any pair of jeans.
      return step(mod(j - r, 3.0), 1.5);
    }
    // Satin. Four over, one under. The move of 2 (rather than 1) is what
    // makes this a satin and not just a wider twill: the single tie-down
    // points never touch their neighbours, so no diagonal forms and the
    // surface reads as unbroken float.
    return 1.0 - step(mod(j - 2.0 * r, 5.0), 0.5);
  }

  float safeZoneWeftOpacity(vec2 ndc) {
    vec2 s = vec2(ndc.x * uAspect, ndc.y);
    vec2 q = abs(s - uSafeCenter) - uSafeHalfSize;
    float d = length(max(q, 0.0)) + min(max(q.x, q.y), 0.0);
    return mix(${SAFE_ZONE_WEFT_OPACITY.toFixed(2)}, 1.0, smoothstep(0.0, uSafeFeather, d));
  }

  void main() {
    POSP vec2 p = vRowUv;

    // The row on the loom right now is revealed in its direction of
    // travel - the thread is pulled across, it does not appear all at
    // once. (The little boat-shuttle sprite that used to ride this front
    // was removed: at TV size it read as an insect crawling on the cloth.) Every other row is already complete and this test costs
    // them one comparison.
    if (vCurrent > 0.5) {
      float reached = vDir > 0.0 ? uCurrentFrac : 1.0 - uCurrentFrac;
      if (vDir > 0.0 ? (p.x > reached) : (p.x < reached)) discard;
    }

    float s = 1.0;
    if (uThicknessNoise > 0.5) {
      s = 1.0 + ${THICKNESS_VARIATION.toFixed(3)} * (noise11(p.x * 9.0 + vSeed * 17.0) * 2.0 - 1.0);
    }

    // The selvedge: the outermost half-spacing at each edge, where the thread
    // turns back on itself and the border comes out denser than the field.
    float edgeBand = 0.5 / uWarpCount;
    float selv = uSelvedge > 0.5
      ? 1.0 - smoothstep(0.0, edgeBand, min(p.x, 1.0 - p.x))
      : 0.0;
    float halfY = vHalf * s * (1.0 + ${SELVEDGE_THICKEN.toFixed(2)} * selv);

    float aaY = 2.0 / max(uPixelHeight, 1.0); // one device pixel, in clip y
    float body = 1.0 - smoothstep(halfY - aaY, halfY + aaY, abs(p.y));

    // The turn itself: at the edge it finished on, a completed row climbs a
    // full pitch to become the next one. That vertical link at the border is
    // the difference between woven cloth and a stack of loose threads.
    float turn = 0.0;
    if (uSelvedge > 0.5 && vCurrent < 0.5) {
      float cx = vDir > 0.0 ? 1.0 - edgeBand * 0.5 : edgeBand * 0.5;
      // Same thread, measured across x instead of y: clip-y spans 2 units
      // over uPixelHeight, u spans 1 unit over uPixelWidth.
      float halfX = halfY * uPixelHeight / (2.0 * max(uPixelWidth, 1.0));
      float aaX = 1.0 / max(uPixelWidth, 1.0);
      float inX = 1.0 - smoothstep(halfX - aaX, halfX + aaX, abs(p.x - cx));
      float t = p.y / vPitch;
      turn = inX * step(0.0, t) * step(t, 1.0);
    }

    float cover = max(body, turn);
    if (cover <= 0.004) discard;

    // THE CROSSING. Over a warp thread the weft is either in front of it or
    // genuinely not there - the warp underneath shows through the hole. This
    // is a discard on purpose: fading the weft to reveal the warp would blend
    // the two colours and read as one translucent thread rather than two
    // solid ones passing each other.
    //
    // Kept in cell-local coordinates (the offset from a cell centre, always
    // small) rather than comparing two values near 1.0, so the ~0.001-wide
    // cut band survives a fragment stage that really is 16-bit.
    POSP float xw = p.x * uWarpCount;
    POSP float j = floor(xw);
    POSP float offset = (xw - j - 0.5) / uWarpCount;
    if (abs(offset) < uWeftCutHalfU && weaveOver(j, vPhase) < 0.5) discard;

    gl_FragColor = vec4(vColor, cover * safeZoneWeftOpacity(vNdc));
  }
`;

// One unit quad, shared by every instanced layer here.
function unitQuadGeometry() {
  const geometry = new THREE.InstancedBufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute([
    -0.5, -0.5, 0, 0.5, -0.5, 0, 0.5, 0.5, 0, -0.5, 0.5, 0
  ], 3));
  geometry.setIndex([0, 1, 2, 0, 2, 3]);
  return geometry;
}

export class LoomVisualizer extends Visualizer {
  constructor(options) {
    super();
    const tier = options && options.qualityTier != null ? options.qualityTier : DEFAULT_TIER;
    this._tier = TIER[tier] ? tier : DEFAULT_TIER;

    // ?weave=toile|serge|satin - forces an armure, for looking at one weave
    // against another on the same track instead of waiting for a track whose
    // syncopation happens to land there.
    const weaveParam = (urlParam('weave') || '').toLowerCase();
    if (weaveParam === 'toile' || weaveParam === 'plain') this._forcedWeave = WEAVE_PLAIN;
    else if (weaveParam === 'serge' || weaveParam === 'sergé' || weaveParam === 'twill') this._forcedWeave = WEAVE_TWILL;
    else if (weaveParam === 'satin') this._forcedWeave = WEAVE_SATIN;
    else this._forcedWeave = null;

    const rowsParam = parseInt(urlParam('rows'), 10);
    this._forcedRows = Number.isFinite(rowsParam) && rowsParam >= 8 && rowsParam <= 400 ? rowsParam : null;

    // ?fastweave=1 - the whole cloth in 20s instead of a track duration, so
    // the finished piece can actually be looked at without sitting through
    // the song. Drives the same row scheduler; only the clock changes.
    this._fastWeave = urlParam('fastweave') === '1';
  }

  init(scene, camera) {
    this._scene = scene;
    this._camera = camera;

    const layout = TIER[this._tier];
    this._rows = this._forcedRows != null ? this._forcedRows : layout.rows;
    this._rowPitch = 2.0 / this._rows;

    this._pixelWidth = 1920;
    this._pixelHeight = 1080;

    const paperTexture = getPaperTexture();
    const paperTint = tintOf(PAPER_TINT_HEX);
    this._paperUniforms = {
      uPaper: { value: paperTexture },
      uHasPaper: { value: paperTexture ? 1 : 0 },
      uPaperTint: { value: new THREE.Vector3(paperTint[0], paperTint[1], paperTint[2]) },
      uPaperLevel: { value: PAPER_LEVEL_LINEAR },
      uPaperGrain: { value: PAPER_GRAIN_GAIN },
      uPaperMean: { value: PAPER_MEAN_LINEAR }
    };
    this._paperGeometry = new THREE.PlaneGeometry(1, 1);
    this._paperMaterial = new THREE.ShaderMaterial({
      uniforms: this._paperUniforms,
      vertexShader: paperVertexShader,
      fragmentShader: paperFragmentShader,
      transparent: false,
      depthTest: false,
      depthWrite: false
    });
    this._paperMesh = new THREE.Mesh(this._paperGeometry, this._paperMaterial);
    this._paperMesh.frustumCulled = false;
    this._paperMesh.renderOrder = -1000;
    scene.add(this._paperMesh);

    // --- warp -------------------------------------------------------------
    // Allocated at the tier's ceiling once; only instanceCount moves when a
    // track's own density asks for a coarser sley.
    const warpTint = tintOf(WARP_TINT_HEX);
    this._warpCapacity = TIER[this._tier].maxWarp;
    this._warpData = new Float32Array(this._warpCapacity * 3);
    this._warpAttribute = new THREE.InstancedBufferAttribute(this._warpData, 3);
    this._warpAttribute.setUsage(THREE.DynamicDrawUsage);
    this._warpGeometry = unitQuadGeometry();
    this._warpGeometry.setAttribute('iWarp', this._warpAttribute);
    this._warpGeometry.instanceCount = 0;
    this._warpUniforms = {
      uPixelWidth: { value: this._pixelWidth },
      uWarpColor: {
        value: new THREE.Vector3(
          warpTint[0] * WARP_LEVEL_LINEAR,
          warpTint[1] * WARP_LEVEL_LINEAR,
          warpTint[2] * WARP_LEVEL_LINEAR
        )
      }
    };
    this._warpMaterial = new THREE.ShaderMaterial({
      uniforms: this._warpUniforms,
      vertexShader: warpVertexShader,
      fragmentShader: warpFragmentShader,
      transparent: true,
      blending: THREE.NormalBlending,
      depthTest: false,
      depthWrite: false
    });
    this._warpMesh = new THREE.Mesh(this._warpGeometry, this._warpMaterial);
    this._warpMesh.frustumCulled = false;
    this._warpMesh.renderOrder = -999;
    scene.add(this._warpMesh);

    // --- weft -------------------------------------------------------------
    // Every attribute is allocated for the full row count here, at startup,
    // and a new row writes ONE instance's worth into it. A finished row is
    // never touched again: it is not redrawn from a model each frame, it is
    // simply a set of numbers already sitting in a buffer.
    this._weftCapacity = this._rows;
    this._rowData = new Float32Array(this._weftCapacity * 4);
    this._rowColors = new Float32Array(this._weftCapacity * 3);
    this._rowDataAttribute = new THREE.InstancedBufferAttribute(this._rowData, 4);
    this._rowColorAttribute = new THREE.InstancedBufferAttribute(this._rowColors, 3);
    this._rowDataAttribute.setUsage(THREE.DynamicDrawUsage);
    this._rowColorAttribute.setUsage(THREE.DynamicDrawUsage);
    this._weftGeometry = unitQuadGeometry();
    this._weftGeometry.setAttribute('iRowData', this._rowDataAttribute);
    this._weftGeometry.setAttribute('iColor', this._rowColorAttribute);
    this._weftGeometry.instanceCount = 0;

    this._weftUniforms = Object.assign({}, safeZoneUniforms, {
      uRowPitch: { value: this._rowPitch },
      // Tall enough for the thickest possible thread AND for the selvedge
      // turn, which climbs a full pitch to reach the next row.
      uHalfSlot: { value: this._rowPitch * 1.06 },
      uClothOffset: { value: 0 },
      uCurrentRow: { value: -1 },
      uCurrentFrac: { value: 0 },
      uWarpCount: { value: WARP_MIN },
      uWeftCutHalfU: { value: WEFT_CUT_HALF_PX / this._pixelWidth },
      uPixelWidth: { value: this._pixelWidth },
      uPixelHeight: { value: this._pixelHeight },
      uThicknessNoise: { value: layout.thicknessNoise ? 1 : 0 },
      uSelvedge: { value: layout.selvedge ? 1 : 0 },
      uWeave: { value: WEAVE_PLAIN }
    });
    this._weftMaterial = new THREE.ShaderMaterial({
      uniforms: this._weftUniforms,
      vertexShader: weftVertexShader,
      fragmentShader: weftFragmentShader,
      transparent: true,
      blending: THREE.NormalBlending,
      depthTest: false,
      depthWrite: false
    });
    this._weftMesh = new THREE.Mesh(this._weftGeometry, this._weftMaterial);
    this._weftMesh.frustumCulled = false;
    this._weftMesh.renderOrder = -998;
    scene.add(this._weftMesh);

    // --- state ------------------------------------------------------------
    this._time = 0;
    this._rowsStarted = 0;
    this._clothOffset = 0;
    this._state = 'weaving';
    this._rollStart = 0;
    this._lastTrackId = undefined;
    this._lastPaletteRef = undefined;
    this._dressedFromCharacter = false;
    this._trackClockStart = 0; // for ?fastweave / radio, reset per track

    this._buildShelf(FALLBACK_PALETTE);
    this._walk = 0.5;
    this._walkDir = 1;
    this._scratchColor = [0, 0, 0];

    this._resolveWarpCount(null, '');
    this._buildWarp('');
    this._pickWeave(null, '');
    this._syncViewportUniforms();
  }

  // Warm to cool. bands.low takes the warmest dye on the shelf and bands.high
  // the coolest, which is the association every listener already has and the
  // one the brief asks for.
  // The yarn shelf: EVERY colour the cover gave, converted once into the
  // textile register and laid out warm to cool. This is the link to the
  // artwork, and it is the whole shelf rather than a few picks from it -
  // an earlier version keyed the three bands to three fixed anchors (warmest,
  // median, coolest), which meant the other two colours of a five-colour
  // cover could never appear in the cloth at all. Here the bands choose a
  // POSITION along the shelf instead, so every cone on it gets woven.
  //
  // Each cone is stored as the sRGB it will actually be woven at - lightness
  // already normalized into the matte band. A row landing BETWEEN two cones
  // mixes those two sRGB values, which is what plying two yarns together
  // does: the blend desaturates toward a heather and stays inside the two
  // colours it came from.
  //
  // Interpolating the HUE instead (the obvious move, and what this did first)
  // is wrong here for a specific reason. The shelf is sorted by warmth, not
  // by hue, so two adjacent cones can be most of the colour wheel apart - a
  // crimson next to a teal, say. Rotating between those along the shorter arc
  // sweeps the rows through yellow and green, and a broad green band appears
  // in the cloth of a cover that contains no green at all. That is precisely
  // the link to the artwork this is supposed to be making, broken. Mixing
  // instead can only ever produce a colour between two the cover really has.
  _buildShelf(hexes) {
    const sorted = hexes.slice().sort(function (a, b) {
      const wa = ((a >> 16) & 255) - (a & 255);
      const wb = ((b >> 16) & 255) - (b & 255);
      return wb - wa; // warm first, cool last
    });
    const hsl = sorted.map(function (hex) {
      const c = hexToRgb(hex);
      return rgbToHsl(c[0], c[1], c[2]);
    });
    let lMin = Infinity, lMax = -Infinity;
    for (let i = 0; i < hsl.length; i++) {
      if (hsl[i][2] < lMin) lMin = hsl[i][2];
      if (hsl[i][2] > lMax) lMax = hsl[i][2];
    }
    const mid = (lMin + lMax) / 2;
    const span = Math.max(lMax - lMin, PALETTE_L_SPAN_FLOOR);
    this._shelf = hsl.map(function (c) {
      return hslToRgb(c[0], c[1], lerp(WEFT_L_MIN, WEFT_L_MAX, clamp01(0.5 + (c[2] - mid) / span)));
    });
  }

  // One yarn off the shelf at position t (0 = warmest cone, 1 = coolest),
  // dyed at a strength the instant's energy sets, and returned as linear
  // colour already under the ceiling.
  _yarnAt(out, t, energy) {
    const n = this._shelf.length;
    const x = clamp01(t) * (n - 1);
    const i = Math.min(n - 2, Math.floor(x));
    const raw = x - i;
    // Eased, so a row spends more of its time ON a cone than between two.
    // Still one continuous curve with no threshold anywhere in it - a weaver
    // works off one cone for a while and changes over, they do not slide
    // uniformly from the first colour of the shelf to the last.
    const f = raw * raw * (3 - 2 * raw);
    const a = this._shelf[i];
    const b = this._shelf[i + 1];
    const hsl = rgbToHsl(lerp(a[0], b[0], f), lerp(a[1], b[1], f), lerp(a[2], b[2], f));
    // "La saturation vient de l'energie globale de l'instant" - applied to the
    // mix's own saturation, so the heather between two cones stays a heather
    // and a cone at full energy stays the cover's own colour.
    const sat = clamp(hsl[1] * lerp(WEFT_SAT_SCALE_MIN, WEFT_SAT_SCALE_MAX, clamp01(energy)), 0, WEFT_SAT_MAX);
    const rgb = hslToRgb(hsl[0], sat, hsl[2]);
    out[0] = srgbToLinear(rgb[0]);
    out[1] = srgbToLinear(rgb[1]);
    out[2] = srgbToLinear(rgb[2]);
    return capLuminance(out);
  }

  // The whole loom is dressed once per track: sley (warp count) and armure
  // (weave). Both are set up before the first pick and neither moves again,
  // because neither moves on a real loom without cutting the cloth off it.
  _resolveWarpCount(character, trackId) {
    // character.density is the brief's name for it; the analysis timeline
    // carries no such field (see track-analysis.js) and percussiveness is
    // exactly what adaptive-party.js already forwards to the orchestrator
    // UNDER the name density - the same number halftone.js/manuscript.js
    // read, named honestly here rather than inventing a field.
    const density = character && character.percussiveness != null
      ? character.percussiveness
      : hashSeed('sley' + trackId);
    const max = TIER[this._tier].maxWarp;
    this._warpCount = Math.max(2, Math.round(lerp(WARP_MIN, max, clamp01(density))));
    this._weftUniforms.uWarpCount.value = this._warpCount;
  }

  _buildWarp(trackId) {
    // Widths and shades are drawn from a hash of the track rather than
    // Math.random, so the same track re-dresses the loom the same way.
    for (let i = 0; i < this._warpCount; i++) {
      const r1 = hashSeed('w' + i + trackId);
      const r2 = hashSeed('s' + i + trackId);
      this._warpData[i * 3 + 0] = (i + 0.5) / this._warpCount;
      this._warpData[i * 3 + 1] = lerp(WARP_WIDTH_PX_MIN, WARP_WIDTH_PX_MAX, r1) * 0.5;
      this._warpData[i * 3 + 2] = r2;
    }
    this._warpAttribute.addUpdateRange(0, this._warpCount * 3);
    this._warpAttribute.needsUpdate = true;
    this._warpGeometry.instanceCount = this._warpCount;
  }

  _pickWeave(character, trackId) {
    if (this._forcedWeave != null) {
      this._weftUniforms.uWeave.value = this._forcedWeave;
      return;
    }
    const sync = character && character.syncopation != null
      ? character.syncopation
      // No analysis: still a stable choice per track rather than a coin
      // flip that changes on reload. A cloth's armure is a property of the
      // piece, not of the session it was looked at in.
      : hashSeed('weave' + trackId);
    this._weftUniforms.uWeave.value = sync > SYNCOPATION_SATIN_ABOVE
      ? WEAVE_SATIN
      : (sync > SYNCOPATION_TWILL_ABOVE ? WEAVE_TWILL : WEAVE_PLAIN);
  }

  _maybeAdoptPalette(profile) {
    if (profile.artworkPalette === this._lastPaletteRef) return;
    this._lastPaletteRef = profile.artworkPalette;
    const vivid = profile.artworkPalette && profile.artworkPalette.vividColors;
    this._buildShelf(vivid && vivid.length >= 3 ? vivid : FALLBACK_PALETTE);
    // Rows already woven keep the dye they were woven with. A palette that
    // arrives mid-cloth changes what comes next, exactly as a new cone of
    // yarn would - it does not go back and re-dye the piece.
  }

  // The colour of ONE row, at the one instant it is woven. Everything that
  // follows is written into a buffer and never revisited.
  _writeRow(rowIndex, colourRgb, low, seed) {
    const slot = rowIndex % this._weftCapacity;

    // "Un passage grave donne un fil epais, un passage aigu un fil fin."
    const thickness = lerp(THREAD_PITCH_FRAC_MIN, THREAD_PITCH_FRAC_MAX, clamp01(low));
    const jitterPx = (Math.random() * 2 - 1) * TENSION_JITTER_PX;

    this._rowData[slot * 4 + 0] = rowIndex;
    this._rowData[slot * 4 + 1] = jitterPx * 2 / this._pixelHeight;
    this._rowData[slot * 4 + 2] = this._rowPitch * thickness * 0.5;
    this._rowData[slot * 4 + 3] = seed;
    this._rowColors[slot * 3 + 0] = colourRgb[0];
    this._rowColors[slot * 3 + 1] = colourRgb[1];
    this._rowColors[slot * 3 + 2] = colourRgb[2];

    // One partial upload per attribute per row - never a full re-upload of
    // the cloth, and nothing at all on a frame that adds no row.
    this._rowDataAttribute.addUpdateRange(slot * 4, 4);
    this._rowDataAttribute.needsUpdate = true;
    this._rowColorAttribute.addUpdateRange(slot * 3, 3);
    this._rowColorAttribute.needsUpdate = true;

    this._weftGeometry.instanceCount = Math.min(rowIndex + 1, this._weftCapacity);
  }

  // Where on the shelf this instant sits. The three bands are the shelf's
  // three landmarks - low at the warm end, mid at its middle, high at the
  // cool end - and the row lands at their weighted centroid, so it is
  // interpolated and never thresholded: a row is allowed to fall between two
  // cones, and most of them do. What the weighting is raised to a power for
  // is reach (see BAND_DOMINANCE): with it, a purely low instant lands
  // exactly on the warmest cone and a purely mid one exactly on the median,
  // and the cover's whole range is actually traversed over a track.
  _colourFromBands(out, low, mid, high) {
    const wl = Math.pow(low, BAND_DOMINANCE);
    const wm = Math.pow(mid, BAND_DOMINANCE);
    const wh = Math.pow(high, BAND_DOMINANCE);
    const sum = wl + wm + wh;
    const t = sum > 1e-6 ? (0.5 * wm + wh) / sum : 0.5;
    // "La saturation vient de l'energie globale de l'instant."
    return this._yarnAt(out, t, clamp01((low + mid + high) / 3));
  }

  // "Sans bands: la couleur de chaque rangee est tiree dans la palette avec
  // une marche aleatoire lente." Each row lands near the one below it, so the
  // cloth still comes out in coherent bands - it just is not this track's
  // bands. The step reflects at the ends rather than clamping, which keeps
  // the walk moving instead of parking against a wall.
  _fallbackRow(out, rowIndex) {
    // Slow on purpose - "chaque rangee proche de la precedente". At roughly a
    // twentieth of the shelf per pick the cloth comes out in bands about ten
    // rows deep, the same scale the band-driven path produces, instead of a
    // new colour every row.
    this._walk += this._walkDir * (0.025 + Math.random() * 0.055);
    if (this._walk > 1) { this._walk = 2 - this._walk; this._walkDir = -1; }
    if (this._walk < 0) { this._walk = -this._walk; this._walkDir = 1; }
    if (Math.random() < 0.18) this._walkDir = -this._walkDir;
    // The walk runs over the SAME shelf, so a cover with no analysis behind
    // it still puts all of its own colours into the cloth - only the link to
    // the music is lost, which is exactly what the brief asks for here.
    this._yarnAt(out, this._walk, 0.45 + 0.3 * Math.sin(rowIndex * 0.21));
    // Thickness by noise rather than by a band, per the brief's own fallback.
    const low = 0.5 + 0.5 * Math.sin(rowIndex * 0.37 + Math.sin(rowIndex * 0.13) * 2.0);
    return low;
  }

  _startRow(rowIndex, profile) {
    const director = profile.director;
    const live = !!(director && director.hasReliableGrid && director.bands);
    const seed = Math.random() * 8;
    let low;
    if (live) {
      const b = director.bands;
      low = clamp01(b.low);
      this._colourFromBands(this._scratchColor, clamp01(b.low), clamp01(b.mid), clamp01(b.high));
    } else {
      low = this._fallbackRow(this._scratchColor, rowIndex);
    }
    this._writeRow(rowIndex, this._scratchColor, low, seed);
  }

  _syncViewportUniforms() {
    const dpr = Math.min(typeof window !== 'undefined' ? (window.devicePixelRatio || 1) : 1, 2);
    const w = (typeof window !== 'undefined' ? window.innerWidth : 1920) * dpr;
    const h = (typeof window !== 'undefined' ? window.innerHeight : 1080) * dpr;
    if (w === this._pixelWidth && h === this._pixelHeight) return;
    this._pixelWidth = w;
    this._pixelHeight = h;
    this._warpUniforms.uPixelWidth.value = w;
    this._weftUniforms.uPixelWidth.value = w;
    this._weftUniforms.uPixelHeight.value = h;
    this._weftUniforms.uWeftCutHalfU.value = WEFT_CUT_HALF_PX / w;
  }

  // Dressing the loom for a new piece: a fresh sley, a fresh armure, an empty
  // frame. Called once the finished cloth has wound off the bottom.
  _dressLoom(profile) {
    const character = profile.director ? profile.director.character : null;
    const trackId = profile.trackId || '';
    this._resolveWarpCount(character, trackId);
    this._buildWarp(trackId);
    this._pickWeave(character, trackId);
    this._dressedFromCharacter = !!(character && character.syncopation != null);
    this._rowsStarted = 0;
    this._weftGeometry.instanceCount = 0;
    this._clothOffset = 0;
    this._weftUniforms.uClothOffset.value = 0;
    this._weftUniforms.uCurrentRow.value = -1;
    this._weftUniforms.uCurrentFrac.value = 0;
    this._trackClockStart = this._time;
    this._walk = Math.random();
  }

  // How far up the frame the cloth should have reached by now, 0..1.
  //
  // progress comes from estimatedPosition, not from profile.progress: the raw
  // Sonos position only moves once every 3s poll, and the weft front crossing the
  // frame is the one thing here that has to be smooth. Same reason
  // adaptive-party.js switched its own mark detection over.
  _weaveProgress(profile) {
    if (this._fastWeave) {
      return clamp01((this._time - this._trackClockStart) / FASTWEAVE_S);
    }
    if (profile.duration > 0) {
      const position = profile.estimatedPosition != null ? profile.estimatedPosition : profile.position;
      return clamp01(position / profile.duration);
    }
    // Web radio: no duration to divide up, so the cloth is not paced against
    // anything - a row every RADIO_ROW_S, and it scrolls out of the bottom
    // once it fills the frame (see _targetClothOffset). This is the one mode
    // where the piece has no end, because the stream has none either.
    return null;
  }

  _targetClothOffset() {
    const overflow = this._rowsStarted - this._weftCapacity;
    return overflow > 0 ? -overflow * this._rowPitch : 0;
  }

  update(deltaTime, profile) {
    const paused = profile.playbackState !== 'PLAYING';
    this._syncViewportUniforms();
    this._maybeAdoptPalette(profile);

    // AdaptivePartyVisualizer never forwards onTrackChange to its active
    // sub-visualizer (the finding sumi.js/resonance.js/halftone.js all
    // record), so the track change is polled here instead.
    if (profile.trackId !== this._lastTrackId) {
      this._lastTrackId = profile.trackId;
      if (this._state === 'weaving' && this._rowsStarted > 0) {
        // "L'etoffe terminee descend et sort du cadre par le bas en 2s."
        // Not a fade and not a cut: the piece is wound off, the way it comes
        // off a real loom, and the empty warp is revealed behind it.
        this._state = 'rolling';
        this._rollStart = this._time;
      } else if (this._state === 'weaving') {
        // Nothing on the loom yet (the first track of the session, or a skip
        // during the roll's own dressing): straight to a fresh warp.
        this._dressLoom(profile);
      }
      // Already rolling: a second skip mid-wind is NOT a second wind. The one
      // in progress finishes and dresses for whatever track is current by
      // then - restarting it would snap the half-wound cloth back up.
    }

    if (!paused) this._time += deltaTime;

    if (this._state === 'rolling') {
      const t = clamp01((this._time - this._rollStart) / ROLL_OUT_S);
      const eased = t * t * (3 - 2 * t);
      this._clothOffset = -ROLL_OUT_NDC * eased;
      this._weftUniforms.uClothOffset.value = this._clothOffset;
      if (t >= 1) {
        this._dressLoom(profile);
        this._state = 'weaving';
      }
      return;
    }

    // The analysis landing - see DRESS_GRACE_ROWS. Only ever while the piece
    // is a few picks old, and it restarts that piece rather than altering it.
    if (!this._dressedFromCharacter && this._rowsStarted <= DRESS_GRACE_ROWS) {
      const arrived = profile.director ? profile.director.character : null;
      if (arrived && arrived.syncopation != null) this._dressLoom(profile);
    }

    if (paused) {
      // "La navette s'arrete en place, rien ne s'efface." The clock is not
      // advanced, no row is started, and every uniform keeps the value it
      // had - the frame simply holds.
      return;
    }

    const progress = this._weaveProgress(profile);
    // Capped at the last row: progress reaching exactly 1.0 would otherwise
    // ask for row index `rows`, which wraps onto slot 0 and would rewrite the
    // first row woven - the one thing this piece must never do. The radio
    // path is deliberately NOT capped; there the wrap is the scroll.
    const rowsWanted = progress != null
      ? Math.min(this._rows, Math.floor(progress * this._rows) + 1)
      : Math.floor((this._time - this._trackClockStart) / RADIO_ROW_S) + 1;

    // Rows only ever go up. A position smoother re-anchoring backwards, or a
    // track restarting its own progress, must never unweave anything.
    let toAdd = Math.min(MAX_ROWS_PER_FRAME, rowsWanted - this._rowsStarted);
    while (toAdd > 0) {
      this._startRow(this._rowsStarted, profile);
      this._rowsStarted++;
      toAdd--;
    }

    if (this._rowsStarted === 0) return;

    const currentRow = this._rowsStarted - 1;
    const frac = progress != null
      ? clamp01(progress * this._rows - currentRow)
      : clamp01(((this._time - this._trackClockStart) / RADIO_ROW_S) - currentRow);

    // The only per-frame writes on a normal frame: which row is on the loom
    // and how far across it the weft front has got. Both plain scalars; not
    // one byte of the cloth itself is touched.
    if (this._weftUniforms.uCurrentRow.value !== currentRow) {
      this._weftUniforms.uCurrentRow.value = currentRow;
    }
    this._weftUniforms.uCurrentFrac.value = frac;

    const targetOffset = this._targetClothOffset();
    if (targetOffset !== this._clothOffset) {
      const k = 1 - Math.exp(-deltaTime / SCROLL_TAU_S);
      this._clothOffset = lerp(this._clothOffset, targetOffset, k);
      if (Math.abs(this._clothOffset - targetOffset) < 1e-5) this._clothOffset = targetOffset;
      this._weftUniforms.uClothOffset.value = this._clothOffset;
    }

  }

  dispose() {
    this._scene.remove(this._paperMesh);
    this._scene.remove(this._warpMesh);
    this._scene.remove(this._weftMesh);
    this._paperGeometry.dispose();
    this._warpGeometry.dispose();
    this._weftGeometry.dispose();
    this._paperMaterial.dispose();
    this._warpMaterial.dispose();
    this._weftMaterial.dispose();
    // uPaper is the shared preloaded sheet (see paper-texture.js) - the same
    // object sumi/halftone/manuscript render on. Never disposed from here.
  }
}
